import { useEffect, useState } from 'react';
import './ShopDelivery.css';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import ReactLoading from 'react-loading';
import { toast } from 'react-toastify';
import { IoMdArrowRoundBack } from "react-icons/io";
import { NumericFormat } from 'react-number-format';
import { useTelegram } from '../../../hooks/auth/useTelegram';
import config from '../../../config';
import api from '../../../hooks/api';
import { FaTruckFast } from "react-icons/fa6";
import StoreBanner from '../Shop/StoreBanner';
import TelegramSafeArea from '../../../Components/TelegramSafeArea';

// --- helper: نمایش اعداد فارسی (فقط UI) ---
const toFa = (n, isCurrency = false) => {
    if (n === undefined || n === null) return '';

    if (isCurrency || typeof n === 'number') {
        const str = Number(n).toLocaleString('en-US'); 
        return str.replace(/[0-9]/g, c =>
            String.fromCharCode(c.charCodeAt(0) + 1728)
        );
    }

    return n.toString().replace(/[0-9]/g, c =>
        String.fromCharCode(c.charCodeAt(0) + 1728)
    );
};

const themeColors = [
    { id: 1, title: "قرمز", code: "#D32F2F", text: "#000" },
    { id: 2, title: "سبز", code: "#388E3C", text: "#000" },
    { id: 3, title: "زرد", code: "#FBC02D", text: "#000" },
    { id: 4, title: "صورتی", code: "#C2185B", text: "#000" },
    { id: 5, title: "آبی", code: "#1976D2", text: "#000" },
    { id: 6, title: "بنفش", code: "#7B1FA2", text: "#000" },
    { id: 7, title: "نارنجی", code: "#E64A19", text: "#000" },
    { id: 8, title: "طوسی", code: "#212121", text: "#000" },
];

function ShopDelivery() {
    const navigate = useNavigate();

    const [store, setStore] = useState(() => {
        try { return JSON.parse(sessionStorage.getItem("store")); } catch (e) { return null; }
    });

    const [cartItems] = useState((JSON.parse(localStorage.getItem("baskets")) ?? [])?.find((x) => x.id === store?.id)?.cartItems ?? []);

    const [dataRestored, setDataRestored] = useState(false);
    const [isRegistered, setIsRegistered] = useState(true);

    const [Address, setAddress] = useState("");
    const [Postcode, setPostcode] = useState("");
    const [shippingMethods, setShippingMethods] = useState([]);
    const [selectedMethod, setSelectedMethod] = useState(null);
    const [shippingPaymentType, setShippingPaymentType] = useState(null);

    const [loadingMethods, setLoadingMethods] = useState(true);

    const [backgroundcolor, setBackgroundcolor] = useState(null);
    const [color, setColor] = useState(null);
    const [bgLight, setBgLight] = useState(null);
    
    const [chatId, setChatId] = useState(null);
    const { ChatId } = useTelegram();

    useEffect(() => {
        if (store) {
            if (store.theme_json) {
                try {
                    const themeData = typeof store.theme_json === 'string' ? JSON.parse(store.theme_json) : store.theme_json;
                    const colorId = themeData?.color_id;

                    const selectedColor = themeColors.find(c => c.id === Number(colorId));

                    if (selectedColor) {
                        setBackgroundcolor(selectedColor.code);
                        setColor(selectedColor.text);
                        setBgLight(`${selectedColor.code}1A`);
                    }
                } catch (error) {
                    console.error("خطا در پارس theme_json", error);
                }
            }
        }
    }, [store]);

    useEffect(() => {
        setChatId(ChatId);
    }, [ChatId]);

    useEffect(() => {
        const initData = async () => {
            if (!store?.id) return;

            const cachedDataStr = sessionStorage.getItem(`delivery_state_${store.id}`);
            let restoredSuccess = false;

            if (cachedDataStr) {
                try {
                    const parsed = JSON.parse(cachedDataStr);
                    if (parsed.shippingMethods && parsed.shippingMethods.length > 0) {
                        setAddress(parsed.address || "");
                        setPostcode(parsed.postcode || "");
                        setShippingMethods(parsed.shippingMethods);
                        setSelectedMethod(parsed.selectedMethod || null);
                        setShippingPaymentType(parsed.shippingPaymentType || null);

                        setDataRestored(true);
                        setLoadingMethods(false);
                        restoredSuccess = true;
                    }
                } catch (e) { console.error("Error parsing cache:", e); }
            }

            if (!restoredSuccess) {
                fetchShippingMethods();
            }
        };

        initData();
    }, [store?.id]);

    useEffect(() => {
        const fetchUserData = async () => {
            if (ChatId && !dataRestored) {
                try {
                    const response = await api.post('user/find');

                    if (response.data && response.data.success) {
                        const userData = response.data.data;
                        if (userData) {
                            if (userData.address) setAddress(userData.address);
                            if (userData.post_code) setPostcode(userData.post_code);
                        }
                        setIsRegistered(true);
                    } else {
                        setIsRegistered(false);
                    }
                } catch (error) {
                    console.error("Error fetching user data:", error);
                    setIsRegistered(false);
                }
            }
        };

        fetchUserData();
    }, [ChatId, dataRestored]);

    useEffect(() => {
        if (!store?.id) return;
        if (shippingMethods.length > 0) {
            const stateToSave = {
                address: Address,
                postcode: Postcode,
                shippingMethods: shippingMethods,
                selectedMethod: selectedMethod,
                shippingPaymentType: shippingPaymentType
            };
            sessionStorage.setItem(`delivery_state_${store.id}`, JSON.stringify(stateToSave));
        }
    }, [Address, Postcode, shippingMethods, selectedMethod, shippingPaymentType, store?.id]);

    const fetchShippingMethods = async () => {
        if (!store?.id) return;
        try {
            setLoadingMethods(true);
            const response = await api.post('post/center/value/active', { business_id: store.id });

            if (response.data && response.data.success) {
                setShippingMethods(response.data.data);
            } else {
                setShippingMethods([]);
            }
            setLoadingMethods(false);
        } catch (error) {
            console.error("Error fetching shipping methods:", error);
            setLoadingMethods(false);
            setShippingMethods([]);
        }
    };

    const handleMethodSelect = (method) => {
        setSelectedMethod(method);
        setShippingPaymentType(null);

        const hasPre = method.prepayment == "1";
        const hasAfter = method.afterpayment == "1";

        if (hasPre && !hasAfter) {
            setShippingPaymentType('pre');
        } else if (!hasPre && hasAfter) {
            setShippingPaymentType('after');
        }
    };

    // بررسی کامل بودن فیلدها و معتبر بودن فرم
    const isFormValid = Boolean(
        !loadingMethods &&
        Address &&
        Address.trim().length >= 5 &&
        Postcode &&
        Postcode.trim().length === 10 &&
        selectedMethod &&
        shippingPaymentType
    );

    const handleContinue = () => {
        if (!Address || Address.trim().length < 5) {
            toast.error("لطفا آدرس دقیق را وارد کنید (حداقل ۵ کاراکتر)");
            return;
        }
        if (!Postcode || Postcode.trim().length !== 10) {
            toast.error("کد پستی باید دقیقاً ۱۰ رقم باشد");
            return;
        }
        if (!selectedMethod) {
            toast.error("لطفا یک شیوه ارسال را انتخاب کنید");
            return;
        }
        if (!shippingPaymentType) {
            toast.error("لطفا نوع پرداخت هزینه ارسال را مشخص کنید");
            return;
        }

        navigate('/ShopPayment', {
            state: {
                address: Address,
                postcode: Postcode,
                shippingMethod: selectedMethod,
                shippingPaymentType: shippingPaymentType
            }
        });
    };

    return (
        <>
            {(isRegistered && (!store || cartItems.length === 0)) && <Navigate to="/" replace={true} />}

            <div className='shop-delivery' style={{ backgroundColor: bgLight || '#F9F9F9', minHeight: '100vh' }}>
                <div className="topbar">
                    <TelegramSafeArea style={{ background: "transparent", color: "#000" }}>
                        <div
                            className="safe-area-title"
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                lineHeight: '1.3'
                            }}
                        >
                            {store?.title ? (
                                <span style={{ fontSize: '15px', fontWeight: 'bold' }}>{store.title}</span>
                            ) : (
                                <div className="skeleton-shimmer" style={{ width: '80px', height: '14px', borderRadius: '4px', marginBottom: '4px' }}></div>
                            )}

                            {store?.message ? (
                                <span style={{ fontSize: '12px', opacity: 0.85 }}>{store.message}</span>
                            ) : store ? null : (
                                <div className="skeleton-shimmer" style={{ width: '120px', height: '10px', borderRadius: '4px' }}></div>
                            )}
                        </div>
                    </TelegramSafeArea>

                    <StoreBanner store={store} />
                    
                    <div className="header" style={{ color: color, background: "transparent" }}>
                        <div className="title">جزئیات ارسال</div>
                        <Link to="/ShopBasket">
                            <div className="back">
                                <div className="btn-back"><IoMdArrowRoundBack size={"20px"} /></div>
                            </div>
                        </Link>
                    </div>
                </div>

                <div className="body-shop">
                    <div className="wrraper-shop">
                        {!isRegistered ? (
                            <div className="not-registered-container" style={{ padding: '20px', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
                                <div style={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', padding: '40px 20px', borderRadius: '15px', boxShadow: '0 8px 25px rgba(0,0,0,0.08)', textAlign: 'center', width: '100%', maxWidth: '400px' }}>
                                    <div style={{ fontSize: '45px', marginBottom: '15px' }}>👤</div>
                                    <h3 style={{ color: '#e74c3c', marginBottom: '15px', fontSize: '18px', fontWeight: 'bold' }}>نیازمند ثبت‌نام</h3>
                                    <p style={{ fontSize: '14px', lineHeight: '2', color: '#555', marginBottom: '30px' }}>
                                        کاربر عزیز، برای ثبت سفارش و ادامه مراحل خرید، لطفاً ابتدا فرآیند ثبت‌نام خود را در ربات تکمیل فرمایید.
                                    </p>
                                    <Link to="/" style={{ display: 'inline-block', padding: '12px 35px', backgroundColor: backgroundcolor || '#5693f5', color: '#fff', borderRadius: '10px', textDecoration: 'none', fontWeight: 'bold', boxShadow: '0 4px 10px rgba(86, 147, 245, 0.3)' }}>
                                        بازگشت به خانه
                                    </Link>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="address-section">
                                    <div className="item-address">
                                        <div className='item-title'>آدرس گیرنده:</div>
                                        <div className='item-input'>
                                            {(loadingMethods && !Address) ? (
                                                <div style={{ textAlign: 'center' }}><ReactLoading type={"bubbles"} color={backgroundcolor || '#5693f5'} height={30} width={30} /></div>
                                            ) : (
                                                <input 
                                                    type='text' 
                                                    value={Address || ''}
                                                    onChange={(e) => setAddress(e.target.value)} 
                                                    placeholder="آدرس کامل پستی به همراه پلاک و واحد" 
                                                    required 
                                                />
                                            )}
                                        </div>
                                    </div>

                                    <div className="item-address">
                                        <div className='item-title'>کد پستی:</div>
                                        <div className='item-input'>
                                            {(loadingMethods && !Postcode) ? (
                                                <div style={{ textAlign: 'center' }}><ReactLoading type={"bubbles"} color={backgroundcolor || '#5693f5'} height={30} width={30} /></div>
                                            ) : (
                                                <NumericFormat
                                                    className="input-control"
                                                    placeholder='کد پستی 10 رقمی'
                                                    value={Postcode || ''}
                                                    allowLeadingZeros
                                                    maxLength={10}
                                                    onValueChange={(values) => setPostcode(values.value)}
                                                    required 
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="shipping-section">
                                    <div className="section-title">
                                        انتخاب شیوه ارسال
                                    </div>

                                    {loadingMethods ? (
                                        <div style={{ textAlign: 'center', padding: '10px', color: '#777' }}>در حال دریافت روش‌های ارسال...</div>
                                    ) : shippingMethods.length === 0 ? (
                                        <div style={{ textAlign: 'center', color: 'red', padding: '10px' }}>هیچ روش ارسالی فعال نیست.</div>
                                    ) : (
                                        shippingMethods.map(method => {
                                            const title = method.post_center?.title || "روش ارسال";
                                            const iconUrl = method.post_center?.icon_url;
                                            const price = parseFloat(method.price);
                                            const hasPre = method.prepayment == "1";
                                            const hasAfter = method.afterpayment == "1";

                                            return (
                                                <div
                                                    key={method.id}
                                                    className={`shipping-card ${selectedMethod?.id === method.id ? "active" : ""}`}
                                                    onClick={() => handleMethodSelect(method)}
                                                >
                                                    <div className="shipping-header">
                                                        <div className="shipping-info">
                                                            {iconUrl ? (
                                                                <img
                                                                    src={`${config.api.imageURL}/${iconUrl}`}
                                                                    alt={title}
                                                                    style={{ width: '40px', height: '40px', objectFit: 'contain' }}
                                                                />
                                                            ) : (
                                                                <FaTruckFast color="#555" size={24} />
                                                            )}
                                                            <span style={{ fontWeight: 'bold', marginRight: '10px' }}>{title}</span>
                                                        </div>
                                                        <div className="shipping-price-badge">
                                                            {hasPre && !hasAfter
                                                                ? (price === 0 ? "رایگان" : `${toFa(price, true)} تومان`)
                                                                : hasAfter && !hasPre
                                                                    ? "پس کرایه"
                                                                    : "انتخابی"}
                                                        </div>
                                                    </div>

                                                    {selectedMethod?.id === method.id && hasPre && hasAfter && (
                                                        <div className="payment-type-selector" onClick={(e) => e.stopPropagation()}>
                                                            <div className="radio-option">
                                                                <input
                                                                    type="radio"
                                                                    name={`pt-${method.id}`}
                                                                    id={`pre-${method.id}`}
                                                                    checked={shippingPaymentType === 'pre'}
                                                                    onChange={() => setShippingPaymentType('pre')}
                                                                />
                                                                <label htmlFor={`pre-${method.id}`} className="radio-label">
                                                                    <span>پرداخت آنلاین هزینه ارسال</span>
                                                                    <span className="price-tag">
                                                                        {price === 0 ? "رایگان" : `${toFa(price, true)} تومان`}
                                                                    </span>
                                                                </label>
                                                            </div>
                                                            <div className="radio-option">
                                                                <input
                                                                    type="radio"
                                                                    name={`pt-${method.id}`}
                                                                    id={`after-${method.id}`}
                                                                    checked={shippingPaymentType === 'after'}
                                                                    onChange={() => setShippingPaymentType('after')}
                                                                />
                                                                <label htmlFor={`after-${method.id}`} className="radio-label">
                                                                    <span>پس کرایه (پرداخت در محل)</span>
                                                                    <span className="price-tag" style={{ color: '#777', fontSize: '11px' }}>هزینه بر عهده مشتری</span>
                                                                </label>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })
                                    )}
                                </div>

                                {/* دکمه تایید و ادامه */}
                                <div className="btn-submit-container">
                                    <button
                                        type="button"
                                        className={`btn-submit ${!isFormValid ? 'disabled' : ''}`}
                                        onClick={isFormValid ? handleContinue : undefined}
                                        disabled={!isFormValid}
                                        style={backgroundcolor && isFormValid ? { backgroundColor: backgroundcolor } : undefined}
                                    >
                                        <span>تایید و ادامه</span>
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

export default ShopDelivery;
